import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import { installRuntimeRecovery } from "./lib/runtimeRecovery";
import { installBuildGuard } from "./lib/buildGuard";
import { installServiceWorkerControl } from "./lib/serviceWorkerControl";
import {
  enforcePreviewTokenOnInitialLoad,
  ensurePreviewTokenInCurrentUrl,
  installPreviewHistoryTokenGuard,
  isLovablePreviewEnvironment,
  persistPreviewTokenFromUrl,
} from "./lib/previewEnvironment";
import "./index.css";

// Keep pre-React fallback visible until React has actually rendered

const removeBootFallback = () => {
  const el = document.getElementById("boot-fallback");
  if (el) el.remove();
};

const showBootFallbackError = (title: string, description: string) => {
  const el = document.getElementById("boot-fallback");
  if (!el) return;

  el.innerHTML = `
    <div style="text-align:center;max-width:420px;padding:20px;">
      <p style="font-size:18px;color:#e2e8f0;margin-bottom:12px;">${title}</p>
      <p style="font-size:13px;margin-bottom:16px;">${description}</p>
      <button onclick="location.reload()" style="background:#3b82f6;color:white;border:none;padding:10px 24px;border-radius:6px;cursor:pointer;font-size:14px;">Recarregar</button>
    </div>
  `;
};

try {
  // ── Build identity logging ──
  const buildId = typeof __APP_BUILD_ID__ !== "undefined" ? __APP_BUILD_ID__ : "unknown";
  console.info(`[NetUno:Build] build_id: ${buildId}`);
  console.info(`[NetUno:Build] cache_prefix: netuno-v2`);
  console.info(`[NetUno:Build] app_instance: ${crypto.randomUUID?.() ?? Date.now()}`);

  const isPreviewEnvironment = isLovablePreviewEnvironment();
  persistPreviewTokenFromUrl();

  const didHardRedirectToTokenizedUrl = isPreviewEnvironment
    ? enforcePreviewTokenOnInitialLoad()
    : false;

  if (didHardRedirectToTokenizedUrl) {
    console.info('[NetUno:Preview] Token restaurado com recarga dura antes do bootstrap');
  } else {
    ensurePreviewTokenInCurrentUrl();

    // ── Install infrastructure guards ──
    // Em preview tokenizado, guardas de infraestrutura ficam em modo seguro
    // para evitar conflitos com cache/proxy do ambiente de desenvolvimento.
    const cleanupRuntimeRecovery = isPreviewEnvironment ? () => undefined : installRuntimeRecovery();
    const cleanupBuildGuard = isPreviewEnvironment ? () => undefined : installBuildGuard();
    const cleanupServiceWorkerControl = installServiceWorkerControl({ previewSafeMode: isPreviewEnvironment });
    const cleanupPreviewHistoryGuard = installPreviewHistoryTokenGuard();

    if (isPreviewEnvironment) {
      console.info('[NetUno:Preview] Ambiente de preview detectado — guardas de infraestrutura em modo seguro');
    }

    if (import.meta.hot) {
      import.meta.hot.dispose(() => {
        cleanupRuntimeRecovery();
        cleanupBuildGuard();
        cleanupServiceWorkerControl();
        cleanupPreviewHistoryGuard();
      });
    }

    const BOOT_RENDER_TIMEOUT_MS = 15_000;
    const bootWatchdog = window.setTimeout(() => {
      if (document.getElementById("boot-fallback")) {
        console.error("[Bootstrap] Timeout de renderização — fallback preservado");
        showBootFallbackError(
          "Inicialização mais lenta que o esperado",
          "A aplicação demorou para renderizar. Tente recarregar para restabelecer a sessão."
        );
      }
    }, BOOT_RENDER_TIMEOUT_MS);

    const rootEl = document.getElementById("root")!;
    createRoot(rootEl).render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );

    // Remove fallback somente após o primeiro ciclo de pintura do React
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.clearTimeout(bootWatchdog);
        removeBootFallback();
      });
    });

    console.info("[NetUno:Bootstrap] session_loaded");

    // Log any pre-React errors captured by the inline script
    const bootErrors = (window as any).__NETUNO_BOOT_ERRORS;
    if (bootErrors?.length) {
      console.warn("[Bootstrap] Erros capturados antes do React:", bootErrors);
    }
  }
} catch (err) {
  console.error("[Bootstrap] Falha crítica no mount do React:", err);

  // Show a raw DOM fallback so the user never sees a blank screen
  const root = document.getElementById("root");
  if (root) {
    root.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;background:#0f1923;font-family:system-ui,sans-serif;color:#94a3b8;">
        <div style="text-align:center;max-width:420px;padding:20px;">
          <p style="font-size:18px;color:#e2e8f0;margin-bottom:12px;">Erro ao iniciar aplicação</p>
          <p style="font-size:13px;margin-bottom:16px;">Ocorreu um erro inesperado. Tente recarregar a página.</p>
          <button onclick="localStorage.clear();sessionStorage.clear();location.reload()" style="background:#3b82f6;color:white;border:none;padding:10px 24px;border-radius:6px;cursor:pointer;font-size:14px;">Limpar cache e recarregar</button>
        </div>
      </div>
    `;
  }
}
